import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api, formatApiError } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ClipboardCheck, ShieldCheck } from 'lucide-react';

const SCALE = [1, 2, 3, 4, 5];

export default function MeEvaluations() {
  const { t, i18n } = useTranslation();
  const es = (i18n.language || '').startsWith('es');
  const L = (a, b) => (es ? a : b);
  const [ws, setWs] = useState(null);
  const [detail, setDetail] = useState(null);
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);

  const reload = () => api.get('/evaluator/workspace').then((r) => setWs(r.data)).catch(() => setWs({ grants: [], tasks: [] }));
  useEffect(() => { reload(); }, []);

  const openTask = async (id) => {
    try {
      const { data } = await api.get(`/evaluator/benchmarks/${id}`);
      setDetail(data);
      setAnswers({});
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/evaluator/benchmarks/${detail.id}/responses`, { answers });
      toast.success(L('Observación enviada. Gracias.', 'Observation submitted. Thank you.'));
      setDetail(null);
      reload();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  if (!ws) return <div className="p-8 text-sm text-muted-foreground">…</div>;
  const pending = (ws.tasks || []).filter((x) => !x.submitted && x.authorized);
  const done = (ws.tasks || []).filter((x) => x.submitted);

  if (detail) {
    const total = (detail.items || []).length;
    const answered = Object.keys(answers).length;
    const byComp = {};
    (detail.items || []).forEach((it) => { (byComp[it.competency_code] = byComp[it.competency_code] || []).push(it); });
    return (
      <div className="max-w-3xl mx-auto space-y-5 p-4" data-testid="me-eval-form">
        <div>
          <h1 className="text-xl font-semibold">{L('Observación de competencias', 'Competency observation')}</h1>
          <p className="text-sm text-muted-foreground">
            {detail.employee?.name} · {detail.role_profile_title || detail.employee?.position}
          </p>
          <p className="text-xs text-muted-foreground mt-1">{detail.instructions}</p>
        </div>
        {Object.entries(byComp).map(([code, its]) => (
          <Card key={code} className="p-4 space-y-3">
            <p className="font-semibold text-sm">
              {(es ? detail.competency_names?.[code]?.name_es : detail.competency_names?.[code]?.name) || detail.competency_names?.[code]?.name || code}
            </p>
            {its.map((it) => (
              <div key={it.item_id} className="space-y-1.5" data-testid={`me-eval-item-${it.item_id}`}>
                <p className="text-sm">{es ? (it.text_es || it.text_en) : (it.text_en || it.text_es)}</p>
                <div className="flex flex-wrap items-center gap-1.5">
                  {SCALE.map((v) => (
                    <Button key={v} size="sm" variant={answers[it.item_id] === v ? 'default' : 'outline'}
                            className="h-8 w-9 px-0" onClick={() => setAnswers((a) => ({ ...a, [it.item_id]: v }))}
                            data-testid={`me-eval-${it.item_id}-${v}`}>{v}</Button>
                  ))}
                  <Button size="sm" variant={answers[it.item_id] === 'no_evidence' ? 'secondary' : 'ghost'}
                          className="h-8 text-xs" onClick={() => setAnswers((a) => ({ ...a, [it.item_id]: 'no_evidence' }))}>
                    {L('Sin evidencia', 'No evidence')}
                  </Button>
                </div>
              </div>
            ))}
          </Card>
        ))}
        <div className="flex items-center gap-3 pb-8">
          <p className="text-xs text-muted-foreground">{answered}/{total} {L('respondidos', 'answered')}</p>
          <Button variant="ghost" onClick={() => setDetail(null)}>{t('common.cancel', { defaultValue: 'Cancelar' })}</Button>
          <Button onClick={submit} disabled={saving || answered < total} data-testid="me-eval-submit">
            {saving ? '…' : L('Enviar observación', 'Submit observation')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-5 p-4" data-testid="me-evaluations-page">
      <div>
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-primary" /> {L('Evaluaciones asignadas', 'Assigned evaluations')}
        </h1>
        <p className="text-sm text-muted-foreground">
          {L('Observaciones de competencias que tu organización te autorizó a realizar.',
            'Competency observations your organization authorized you to perform.')}
        </p>
      </div>
      {pending.length === 0 ? (
        <Card className="p-6 text-center text-sm text-muted-foreground" data-testid="me-eval-empty">
          {L('No tienes evaluaciones pendientes.', 'You have no pending evaluations.')}
        </Card>
      ) : pending.map((x) => (
        <Card key={x.id} className="p-4 flex flex-wrap items-center gap-3" data-testid={`me-eval-task-${x.id}`}>
          <ClipboardCheck className="h-5 w-5 text-primary shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="font-medium text-sm">{x.employee?.name}</p>
            <p className="text-xs text-muted-foreground">{x.role_profile_title || x.employee?.position} · {x.competency_count} {L('competencias', 'competencies')} · {x.item_count} {L('indicadores', 'indicators')}</p>
          </div>
          <Button size="sm" onClick={() => openTask(x.id)} data-testid={`me-eval-open-${x.id}`}>
            {L('Evaluar', 'Evaluate')}
          </Button>
        </Card>
      ))}
      {done.length > 0 && (
        <Card className="p-4">
          <p className="text-sm font-medium mb-2">{L('Enviadas', 'Submitted')}</p>
          <ul className="space-y-1">
            {done.map((x) => (
              <li key={x.id} className="text-xs text-muted-foreground flex items-center gap-2">
                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">{L('Enviada', 'Submitted')}</Badge>
                {x.employee?.name}
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
